import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';
import '@videojs/http-streaming';
import { Box, Paper, Typography, IconButton, Tooltip, Snackbar, Alert } from '@mui/material';
import CastIcon from '@mui/icons-material/Cast';
import CastConnectedIcon from '@mui/icons-material/CastConnected';

function Player() {
  const { videoId } = useParams();
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const [error, setError] = useState(null);
  const [isPlayerReady, setIsPlayerReady] = useState(false);
  const [isCasting, setIsCasting] = useState(false);
  const [castSession, setCastSession] = useState(null);
  const [castError, setCastError] = useState(null);

  // Check if we're in a secure context
  const isSecureContext = window.isSecureContext;

  // Initialize Google Cast
  useEffect(() => {
    if (!isSecureContext) {
      console.log('[Cast] Not in a secure context. Chrome Cast requires HTTPS or localhost.');
      return;
    }

    if (!window.chrome?.cast?.isAvailable) {
      console.log('[Cast] Chrome Cast API not available');
      return;
    }

    const initializeCastApi = () => {
      const sessionRequest = new window.chrome.cast.SessionRequest(
        window.chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID
      );
      const apiConfig = new window.chrome.cast.ApiConfig(
        sessionRequest,
        sessionListener,
        receiverListener
      );
      window.chrome.cast.initialize(apiConfig, onInitSuccess, onInitError);
    };

    const sessionListener = (session) => {
      setCastSession(session);
      setIsCasting(true);
      console.log('[Cast] Session listener:', session);
    };

    const receiverListener = (availability) => {
      console.log('[Cast] Receiver listener:', availability);
    };

    const onInitSuccess = () => {
      console.log('[Cast] Cast initialization success');
    };

    const onInitError = (error) => {
      console.error('[Cast] Cast initialization error:', error);
      setCastError('Failed to initialize Chrome Cast. Please make sure you\'re using Chrome browser.');
    };

    // Initialize Cast API
    if (window.chrome.cast.isAvailable) {
      initializeCastApi();
    } else {
      window.__onGCastApiAvailable = function(isAvailable) {
        if (isAvailable) {
          initializeCastApi();
        }
      };
    }
  }, [isSecureContext]);

  const startCasting = async () => {
    if (!isSecureContext) {
      setCastError('Chrome Cast requires a secure connection (HTTPS) or localhost. Please use HTTPS or access the site via localhost.');
      return;
    }

    try {
      const session = await new Promise((resolve, reject) => {
        if (!window.chrome?.cast?.requestSession) {
          reject(new Error('Chrome Cast API not available'));
          return;
        }
        window.chrome.cast.requestSession(resolve, reject);
      });

      const videoSrc = `${process.env.REACT_APP_BACKEND_URL}/uploads/${videoId}/playlist.m3u8`;
      const mediaInfo = new window.chrome.cast.media.MediaInfo(videoSrc, 'application/x-mpegURL');
      
      const request = new window.chrome.cast.media.LoadRequest(mediaInfo);
      await session.loadMedia(request);
      
      setCastSession(session);
      setIsCasting(true);
      console.log('[Cast] Started casting');
    } catch (error) {
      console.error('[Cast] Error starting cast:', error);
      setCastError(error.message || 'Failed to start casting. Please make sure you\'re using Chrome browser and have a ChromeCast device available.');
    }
  };

  const stopCasting = async () => {
    if (castSession) {
      try {
        await castSession.stop();
        setCastSession(null);
        setIsCasting(false);
        console.log('[Cast] Stopped casting');
      } catch (error) {
        console.error('[Cast] Error stopping cast:', error);
        setCastError('Failed to stop casting. Please try again.');
      }
    }
  };

  const handleCloseError = () => {
    setCastError(null);
  };

  useEffect(() => {
    let player = null;

    const initializePlayer = () => {
      const videoElement = videoRef.current;
      
      if (!videoElement) {
        console.log('[Player] Video element not found');
        return;
      }

      if (!document.body.contains(videoElement)) {
        console.log('[Player] Video element not in DOM yet');
        return;
      }

      console.log('[Player] Initializing video.js player');
      player = videojs(videoElement, {
        controls: true,
        fluid: true,
        html5: {
          hls: {
            enableLowInitialPlaylist: true,
            smoothQualityChange: true,
            overrideNative: true,
          },
        },
      });

      playerRef.current = player;

      // Log player events
      const events = [
        'loadstart',
        'loadedmetadata',
        'loadeddata',
        'canplay',
        'playing',
        'waiting',
        'stalled',
        'error',
        'ended'
      ];

      events.forEach(event => {
        player.on(event, () => {
          console.log(`[Player] Event: ${event}`);
        });
      });

      player.on('error', (error) => {
        console.error('[Player] Error:', error);
        setError(`Error loading video: ${error.message || 'Unknown error'}`);
      });

      player.on('hls:manifest-loaded', () => {
        console.log('[Player] HLS manifest loaded');
      });

      player.on('hls:level-switching', (event, data) => {
        console.log('[Player] HLS quality switching:', data);
      });

      const videoSrc = `${process.env.REACT_APP_BACKEND_URL}/uploads/${videoId}/playlist.m3u8`;
      console.log('[Player] Setting video source:', videoSrc);
      
      player.src({
        src: videoSrc,
        type: 'application/x-mpegURL',
      });

      player.on('loadedmetadata', () => {
        console.log('[Player] Video metadata loaded');
        console.log('[Player] Video duration:', player.duration());
        console.log('[Player] Available qualities:', player.qualityLevels?.()?.length || 'unknown');
        setIsPlayerReady(true);
      });
    };

    const timer = setTimeout(() => {
      initializePlayer();
    }, 100);

    return () => {
      clearTimeout(timer);
      if (playerRef.current) {
        try {
          console.log('[Player] Disposing player');
          playerRef.current.dispose();
          playerRef.current = null;
          setIsPlayerReady(false);
        } catch (error) {
          console.error('[Player] Error disposing video player:', error);
        }
      }
    };
  }, [videoId]);

  if (error) {
    return (
      <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, p: 2 }}>
        <Paper elevation={3} sx={{ p: 2 }}>
          <Typography color="error">{error}</Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4 }}>
      <Paper elevation={3}>
        <Box sx={{ position: 'relative' }}>
          <div data-vjs-player>
            <video
              ref={videoRef}
              className="video-js vjs-big-play-centered"
              style={{ width: '100%', aspectRatio: '16/9' }}
              playsInline
            />
          </div>
          {!isPlayerReady && (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography>Loading player...</Typography>
            </Box>
          )}
          <Box sx={{ position: 'absolute', top: 10, right: 10, zIndex: 1 }}>
            <Tooltip title={isCasting ? "Stop Casting" : "Cast to Device"}>
              <IconButton 
                onClick={isCasting ? stopCasting : startCasting}
                sx={{ 
                  bgcolor: 'rgba(0, 0, 0, 0.5)',
                  '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' }
                }}
              >
                {isCasting ? <CastConnectedIcon /> : <CastIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Paper>
      <Snackbar 
        open={!!castError} 
        autoHideDuration={6000} 
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {castError}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Player; 